<template>
  <v-container class="mb-4">
    <v-row dense>
      <v-col cols="12">
        <v-text-field
          label="Suchwort"
          v-model="query"
          append-icon="mdi-magnify"
          outlined
          clearable
        />
      </v-col>
      <v-col v-if="selectedItems.length">
        <v-btn color="primary" @click="showPaidDialog = true">
          Auswahl auf bezahlt setzen
        </v-btn>
        <v-btn color="primary" class="ml-4" @click="showMailDialog = true">
          E-Mail an Auswahl schicken
        </v-btn>
      </v-col>
      <v-col cols="12">
        <v-data-table
          v-model="selectedItems"
          :headers="headers"
          :items="booths"
          :search="query"
          show-select
          :items-per-page="-1"
        >
          <template #[`item.persons`]="{ item }">
            <div
              v-for="(person, index) in item.persons"
              :key="`${item.id}-person-${index}`"
              v-html="person"
            />
          </template>
          <template #[`item.openAmount`]="{ item }">
            <div
              v-for="(openAmount, index) in item.openAmount"
              :key="`${item.id}-open-amount-${index}`"
            >
              {{ openAmount.text }}: {{ openAmount.amount }}
            </div>
          </template>
          <template #[`item.contract`]="{ item }">
            <v-btn color="primary" text @click="openContract(item)">
              Vertrag
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog v-model="showPaidDialog" persistent max-width="300px">
      <v-card>
        <v-card-title class="white--text headline primary">
          Auf bezahlt setzen
        </v-card-title>
        <v-card-text class="title pt-5">
          {{ selectedItems.length }} ausgewählte Stände werden auf bezahlt
          gesetzt
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="setPaid()"> OK </v-btn>
          <v-btn color="primary" text @click="showPaidDialog = false">
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showMailDialog" persistent max-width="600px">
      <v-card>
        <v-card-title class="white--text headline primary">
          E-Mail an {{ selectedItems.length }} ausgewählte Stände schicken
        </v-card-title>
        <v-card-text class="title pt-5">
          <v-text-field label="Betreff" v-model="mailSubject" />
          <ckeditor
            v-model="mailText"
            :config="editorConfig"
            :read-only="updating"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="sendMail()"
            :disabled="!mailSubject || !mailText"
          >
            Senden
          </v-btn>
          <v-btn color="primary" text @click="showMailDialog = false">
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      top
      color="success"
      v-model="mailSuccess"
      :timeout="6000"
      style="z-index: 9999"
    >
      Mailversand erfolgreich gestartet
      <v-btn dark text @click="mailSuccess = false">X</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  boothResource,
  exhibitorPayResource,
  exhibitorMailResource,
} from "../api/resources";
import { API_BASE } from "../config/constants";

export default {
  data: () => ({
    query: "",
    booths: [],
    selectedItems: [],
    showPaidDialog: false,
    showMailDialog: false,
    mailSubject: "",
    mailText: "",
  }),

  created() {
    this.getBooths();
  },

  methods: {
    ...mapActions(["updateExhibitor"]),
    async getBooths() {
      const response = (await boothResource.get("")).data.results;
      const numberFormatter = new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      });
      this.booths = response.map((entry) => ({
        id: entry._id,
        title: entry.title,
        company: [
          entry.vendorName,
          ...(entry.additionalExhibitors || []).map(
            (exhibitor) => exhibitor.vendorName
          ),
        ].join(", "),
        persons: [
          ...(entry.persons || [])
            .map((entry) => this.getBoothPersonName(entry))
            .filter((entry) => entry),
        ],
        email: [
          entry.email,
          ...(entry.additionalExhibitors || []).map(
            (exhibitor) => exhibitor.email
          ),
        ].join(", "),
        openAmount: [
          ...entry.persons
            .filter((person) => !person.paid)
            .map(() => ({
              text: "Zusatzperson",
              amount: numberFormatter.format(
                this.pageSettings.additionalPricePerPerson
              ),
            })),
          ...entry.bills
            .filter((bill) => !bill.paid)
            .map((bill) => ({
              text: "Zwischenrechnung",
              amount: numberFormatter.format(bill.amount),
            })),
        ],
      }));
    },
    async setPaid() {
      this.showPaidDialog = false;
      await exhibitorPayResource.post(
        "",
        {
          ids: this.selectedItems.map((entry) => entry.id),
        },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
      this.getBooths();
    },
    async sendMail() {
      this.showMailDialog = false;
      exhibitorMailResource.post(
        "",
        {
          ids: this.selectedItems.map((entry) => entry.id),
          mailSubject: this.mailSubject,
          mailText: this.mailText,
        },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
      this.mailSubject = "";
      this.mailText = "";
      this.mailSuccess = true;
    },
    getBoothPersonName(person) {
      const names = [];
      if (person.firstName) {
        names.push(person.firstName);
      }
      if (person.lastName) {
        names.push(person.lastName);
      }
      if (!names.length) {
        return "";
      }
      return (
        names.join(" ") + (person.company ? " (" + person.company + ")" : "")
      );
    },
    openContract(booth) {
      window.open(
        `${API_BASE}/api/v1/exhibitors/contract/${booth.id}`,
        "_blank",
        "noopener"
      );
    },
  },

  computed: {
    ...mapGetters(["pageSettings", "editorDefaultToolbar"]),
    editorConfig() {
      return { toolbar: [[...this.editorDefaultToolbar]] };
    },
    headers() {
      const toReturn = [
        { text: "Stand", value: "title" },
        { text: "Firma", value: "company" },
        { text: "Standbetreuer", value: "persons" },
        { text: "E-Mail", value: "email" },
        { text: "Offener Betrag", value: "openAmount" },
        { text: "Vertrag", value: "contract" },
      ];
      return toReturn;
    },
  },
};
</script>
<style scoped></style>
